import { formatOeDisplay } from '@/utils/partDisplay';
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useCart } from '@/context/CartContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { 
  ArrowLeft, Package, ShoppingCart, Eye, ChevronLeft, ChevronRight, 
  RefreshCw, Clock, CheckCircle, Truck, XCircle, Plus, FileDown, Printer, Edit,
  Bell, Box, AlertTriangle, ChevronDown, FileText, ClipboardList, Receipt,
  Download, Upload, ExternalLink, ZoomIn, X
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { PDFDocument } from 'pdf-lib';
import EditOrderModal from '@/components/EditOrderModal';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MyOrdersPage = () => {
  const navigate = useNavigate();
  const { token, user } = useAuth();
  const { addToCart } = useCart();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  
  // Image zoom state
  const [zoomImage, setZoomImage] = useState(null);
  
  // Check if user is from India (for GST label)
  const isIndianCustomer = user?.country === 'India' || 
    (!user?.country && user?.state && !['Nepal', 'Bangladesh'].includes(user?.country));
  
  const [activeTab, setActiveTab] = useState('pending');
  
  // Notifications
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Order details dialog
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDialogOpen, setOrderDialogOpen] = useState(false);
  const [orderDetailsLoading, setOrderDetailsLoading] = useState(false);
  
  // Edit order
  const [editOrder, setEditOrder] = useState(null);
  
  // Document viewer
  const [docViewerOpen, setDocViewerOpen] = useState(false);
  const [docViewerType, setDocViewerType] = useState(null); // 'invoice' or 'lr'
  const [docViewerUrl, setDocViewerUrl] = useState(null);
  const [docViewerOrderId, setDocViewerOrderId] = useState(null);
  const [docViewerLoading, setDocViewerLoading] = useState(false);
  
  // LR Upload
  const [lrUploadOpen, setLrUploadOpen] = useState(false);
  const [lrUploadOrderId, setLrUploadOrderId] = useState(null);
  const [lrUploading, setLrUploading] = useState(false);
  const [lrFormData, setLrFormData] = useState({
    transport_name: '',
    lr_number: '',
    lr_date: '',
    lr_image: null
  });

  const loadOrders = useCallback(async (pageNum = 1) => {
    setLoading(true);
    try {
      // Fetch a generous page size so a single customer's full order
      // history typically arrives in one round-trip. The frontend then
      // splits orders into Pending / Dispatched tabs client-side. If the
      // backend page were small (e.g. 20) and a customer had 6 pending
      // orders interleaved across two server pages, the Pending tab
      // would show 4 on page 1 + 2 on page 2 with empty whitespace.
      // 100 is the server cap and covers ~99% of customers in one shot.
      const response = await axios.get(`${API}/my-orders?page=${pageNum}&limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.orders || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
      setPage(pageNum);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [token]);

  const loadNotifications = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/task-manager/customer/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to load notifications');
    }
  }, [token]);

  // Dismiss a notification
  const dismissNotification = async (notificationId) => {
    try {
      await axios.post(`${API}/task-manager/customer/notifications/${notificationId}/dismiss`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Remove from local state
      setNotifications(prev => prev.filter(n => n.id !== notificationId && n._id !== notificationId));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      // If API doesn't exist yet, just remove locally
      setNotifications(prev => prev.filter(n => n.id !== notificationId && n._id !== notificationId));
    }
  };

  // Dismiss all notifications
  const dismissAllNotifications = async () => {
    try {
      await axios.post(`${API}/task-manager/customer/notifications/dismiss-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications([]);
      setUnreadCount(0);
    } catch (error) {
      // If API doesn't exist yet, just clear locally
      setNotifications([]);
      setUnreadCount(0);
    }
  };

  useEffect(() => {
    loadOrders();
    loadNotifications();
  }, [loadOrders, loadNotifications]);

  const loadOrderDetails = async (orderId) => {
    setOrderDetailsLoading(true);
    setOrderDialogOpen(true);
    try {
      const response = await axios.get(`${API}/my-orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedOrder(response.data);
    } catch (error) {
      toast.error('Failed to load order details');
      setOrderDialogOpen(false);
    } finally {
      setOrderDetailsLoading(false);
    }
  };

  const handleReorder = (item) => {
    addToCart({
      jp_part_no: item.jp_part_no,
      oe_part_no: item.oe_part_no,
      part_name: item.part_name,
      mrp: item.mrp,
      discounted_price: item.discounted_price || item.price || item.mrp,
      has_special_price: item.has_special_price || false
    }, 1);
    toast.success(`${item.part_name} added to cart`);
  };

  const handleReorderAll = () => {
    if (!selectedOrder?.items) return;
    
    selectedOrder.items.forEach(item => {
      addToCart({
        jp_part_no: item.jp_part_no,
        oe_part_no: item.oe_part_no,
        part_name: item.part_name,
        mrp: item.mrp,
        discounted_price: item.discounted_price || item.price || item.mrp,
        has_special_price: item.has_special_price || false
      }, item.quantity);
    });
    toast.success(`All ${selectedOrder.items.length} items added to cart`);
    setOrderDialogOpen(false);
  };

  // ==========================================================================
  // SHARED HELPER: fetch the merged PDF that the admin Document Sharing Panel
  // shares with customers (Invoice + Packing List + LR Copy properly stitched
  // together server-side). Three different routes depending on order type:
  //   - Domestic     → /api/task-manager/admin/order-all-docs-pdf/{order_id}
  //   - Nepal        → /api/nepal-orders/{order_id}/customer-docs-merged
  //   - Bangladesh   → /api/export-orders/{order_id}/customer-docs-merged
  //
  // The backend now authorizes a customer to fetch their OWN orders via these
  // routes (admin-grade renderer, same byte-for-byte PDF the admin panel
  // produces). This eliminates the previous client-side jsPDF fabrication
  // which was missing the LR and using a different packing-list layout.
  // ==========================================================================
  const fetchMergedDocsPdf = async (orderId, orderObj = null) => {
    // Try to infer type from the in-memory order list if not provided
    const ord = orderObj || orders.find(o => o.order_id === orderId) || {};
    const ot = (ord.order_type || ord.type || '').toLowerCase();

    // Build an ordered list of endpoints to try.
    // Domestic first (most common); fallbacks cover Nepal and Bangladesh exports.
    const candidates = [];
    if (ot === 'nepal' || ot === 'export_nepal') {
      candidates.push(`${API}/nepal-orders/${orderId}/customer-docs-merged`);
    } else if (ot === 'export' || ot === 'bangladesh' || ot === 'export_bangladesh') {
      candidates.push(`${API}/export-orders/${orderId}/customer-docs-merged`);
    } else {
      candidates.push(`${API}/task-manager/admin/order-all-docs-pdf/${orderId}`);
    }
    // Add the other two as fallbacks (server returns 404 if order isn't there)
    if (!candidates[0].includes('/admin/order-all-docs-pdf'))
      candidates.push(`${API}/task-manager/admin/order-all-docs-pdf/${orderId}`);
    if (!candidates[0].includes('/nepal-orders'))
      candidates.push(`${API}/nepal-orders/${orderId}/customer-docs-merged`);
    if (!candidates[0].includes('/export-orders'))
      candidates.push(`${API}/export-orders/${orderId}/customer-docs-merged`);

    let lastErr = null;
    for (const url of candidates) {
      try {
        const resp = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
        const d = resp?.data || {};
        // Both endpoints use the field `data` (base64 PDF data URL); the
        // domestic admin endpoint may use `pdf_base64` historically — accept both.
        const pdfData = d.data || d.pdf_base64 || d.pdf || null;
        if (pdfData) return pdfData;
      } catch (e) {
        lastErr = e;
        // 404 means try next endpoint; 403 too (in case admin-only fallback)
        if (e?.response?.status && ![404, 403].includes(e.response.status)) {
          throw e;
        }
      }
    }
    throw lastErr || new Error('No documents available');
  };

  // Download combined PDF (Invoice + Packing List + LR) for closed orders
  // Invoice is always PDF, Packing List is generated, LR can be PDF or image
  const downloadOrderDocuments = async (orderId) => {
    try {
      toast.loading('Preparing documents...', { id: 'dl-all' });
      const pdfData = await fetchMergedDocsPdf(orderId);
      toast.dismiss('dl-all');

      // Save as file
      const link = document.createElement('a');
      link.href = pdfData;  // data:application/pdf;base64,...
      link.download = `${orderId}_Documents.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Documents downloaded');
    } catch (error) {
      toast.dismiss('dl-all');
      console.error('Download all error:', error);
      const msg = error?.response?.data?.detail || error?.message || 'Failed to download documents';
      toast.error(msg);
    }
  };

  /* Legacy client-side merge — preserved only as fallback reference.
     Replaced above by fetchMergedDocsPdf which uses the admin-grade
     server renderer. Safe to delete if no other call sites remain. */
  const downloadOrderDocumentsLegacy = async (orderId) => {
    try {
      toast.loading('Preparing documents...');

      const response = await axios.get(`${API}/task-manager/customer/order-documents/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      
      // Create the final merged PDF document
      const mergedPdf = await PDFDocument.create();
      
      // Helper to convert base64 to Uint8Array
      const base64ToUint8Array = (base64) => {
        // Remove data URL prefix if present
        const base64Data = base64.split(',')[1] || base64;
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes;
      };
      
      // Helper to check if data is a PDF
      const isPDF = (dataUrl) => dataUrl && dataUrl.startsWith('data:application/pdf');
      
      // 1. Add Invoice (PDF) - Always PDF now
      if (data.tally_invoice) {
        if (isPDF(data.tally_invoice)) {
          try {
            const pdfBytes = base64ToUint8Array(data.tally_invoice);
            const invoicePdf = await PDFDocument.load(pdfBytes);
            const pages = await mergedPdf.copyPages(invoicePdf, invoicePdf.getPageIndices());
            pages.forEach(page => mergedPdf.addPage(page));
          } catch (e) {
            console.error('Error adding invoice PDF:', e);
            toast.error('Invoice PDF could not be loaded - may be corrupted');
          }
        } else {
          // Invoice is an image (legacy) - convert to PDF page
          try {
            const page = mergedPdf.addPage();
            const { width, height } = page.getSize();
            
            let image;
            if (data.tally_invoice.startsWith('data:image/png')) {
              image = await mergedPdf.embedPng(base64ToUint8Array(data.tally_invoice));
            } else {
              image = await mergedPdf.embedJpg(base64ToUint8Array(data.tally_invoice));
            }
            
            // Scale image to fit page while maintaining aspect ratio
            const imgDims = image.scale(1);
            const scale = Math.min(width / imgDims.width, height / imgDims.height) * 0.9;
            
            page.drawImage(image, {
              x: (width - imgDims.width * scale) / 2,
              y: (height - imgDims.height * scale) / 2,
              width: imgDims.width * scale,
              height: imgDims.height * scale,
            });
          } catch (e) {
            console.error('Error converting invoice image:', e);
          }
        }
      }
      
      // 2. Generate and add Packing List
      if (data.packing_list?.length > 0) {
        // Generate packing list as jsPDF then convert to pdf-lib
        const packingDoc = new jsPDF();
        let currentY = 15;
        
        packingDoc.setFontSize(16);
        packingDoc.setTextColor(0, 102, 204);
        packingDoc.text('PACKING LIST', 105, currentY, { align: 'center' });
        currentY += 12;
        
        packingDoc.setFontSize(10);
        packingDoc.setTextColor(0, 0, 0);
        packingDoc.text(`Order: ${data.order_id}`, 14, currentY);
        packingDoc.text(`Customer: ${data.customer_name}`, 14, currentY + 6);
        packingDoc.text(`Date: ${formatDate(data.packing_finalized_at)}`, 150, currentY);
        currentY += 18;
        
        const tableData = data.packing_list.map((item, idx) => [
          idx + 1,
          item.jp_part_no,
          item.part_name?.substring(0, 35) || '',
          item.order_qty,
          item.packed_qty,
          item.cartons?.map(c => {
            const wk = Number(c.weight_kg || 0);
            const num = c.carton_no || c.carton_number;
            return wk > 0 ? `${num} (${wk.toFixed(1)}kg)` : `${num}`;
          }).join(', ') || '-'
        ]);
        
        autoTable(packingDoc, {
          startY: currentY,
          head: [['#', 'Part No', 'Description', 'Ordered', 'Packed', 'Cartons (Weight)']],
          body: tableData,
          theme: 'grid',
          styles: { fontSize: 8 },
          headStyles: { fillColor: [0, 102, 204] }
        });
        // Total weight footer
        const _twk = Number(data.total_weight_kg || 0);
        if (_twk > 0) {
          const _fY = packingDoc.lastAutoTable ? packingDoc.lastAutoTable.finalY : currentY + 20;
          packingDoc.setFontSize(9);
          packingDoc.setFont(undefined, 'bold');
          packingDoc.text(`Total Weight: ${_twk.toFixed(2)} kg`, 196, _fY + 6, { align: 'right' });
          packingDoc.setFont(undefined, 'normal');
        }
        
        // Convert jsPDF to pdf-lib compatible format
        const packingPdfBytes = packingDoc.output('arraybuffer');
        const packingPdf = await PDFDocument.load(packingPdfBytes);
        const packingPages = await mergedPdf.copyPages(packingPdf, packingPdf.getPageIndices());
        packingPages.forEach(page => mergedPdf.addPage(page));
      }
      
      // 3. Add LR (PDF or Image)
      const hasLRContent = data.lr_details && (
        data.lr_details.transport_name || 
        data.lr_details.lr_number || 
        data.lr_details.lr_image
      );
      
      if (hasLRContent && data.lr_details.lr_image) {
        if (isPDF(data.lr_details.lr_image)) {
          // LR is PDF - merge directly
          try {
            const pdfBytes = base64ToUint8Array(data.lr_details.lr_image);
            const lrPdf = await PDFDocument.load(pdfBytes);
            const pages = await mergedPdf.copyPages(lrPdf, lrPdf.getPageIndices());
            pages.forEach(page => mergedPdf.addPage(page));
          } catch (e) {
            console.error('Error adding LR PDF:', e);
          }
        } else {
          // LR is image - convert to PDF page
          try {
            const page = mergedPdf.addPage();
            const { width, height } = page.getSize();
            
            let image;
            if (data.lr_details.lr_image.startsWith('data:image/png')) {
              image = await mergedPdf.embedPng(base64ToUint8Array(data.lr_details.lr_image));
            } else {
              image = await mergedPdf.embedJpg(base64ToUint8Array(data.lr_details.lr_image));
            }
            
            // Scale image to fit page while maintaining aspect ratio
            const imgDims = image.scale(1);
            const scale = Math.min(width / imgDims.width, height / imgDims.height) * 0.9;
            
            page.drawImage(image, {
              x: (width - imgDims.width * scale) / 2,
              y: (height - imgDims.height * scale) / 2,
              width: imgDims.width * scale,
              height: imgDims.height * scale,
            });
          } catch (e) {
            console.error('Error converting LR image:', e);
          }
        }
      }
      
      // Save the merged PDF
      const mergedPdfBytes = await mergedPdf.save();
      const blob = new Blob([mergedPdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.download = `${data.customer_name}_${data.order_id}_Documents.pdf`.replace(/[^a-zA-Z0-9_.-]/g, '');
      link.click();
      
      URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('Documents downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.dismiss();
      toast.error('Failed to download documents');
    }
  };

  // Helper to convert Uint8Array to base64 (handles large arrays)
  const uint8ArrayToBase64 = (bytes) => {
    let binary = '';
    const chunkSize = 8192;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, chunk);
    }
    return btoa(binary);
  };

  // View All Documents - merged PDF in viewer (uses admin-grade server renderer)
  const viewAllDocuments = async (orderId) => {
    setDocViewerLoading(true);
    setDocViewerType('all');
    setDocViewerOrderId(orderId);
    setDocViewerOpen(true);

    try {
      toast.loading('Merging documents...', { id: 'merge-docs' });
      const pdfData = await fetchMergedDocsPdf(orderId);
      toast.dismiss('merge-docs');

      // Turn the base64 data URL into a blob URL for the viewer
      const base64 = (pdfData || '').includes(',') ? pdfData.split(',')[1] : pdfData;
      const bytes = Uint8Array.from(atob(base64), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      setDocViewerUrl(blobUrl);
    } catch (error) {
      toast.dismiss('merge-docs');
      console.error('View all error:', error);
      const msg = error?.response?.data?.detail || error?.message || 'Failed to load documents';
      toast.error(msg);
      setDocViewerOpen(false);
    } finally {
      setDocViewerLoading(false);
    }
  };

  /* Legacy client-side viewAllDocuments preserved as reference. */
  const viewAllDocumentsLegacy = async (orderId) => {
    setDocViewerLoading(true);
    setDocViewerType('all');
    setDocViewerOrderId(orderId);
    setDocViewerOpen(true);
    
    try {
      toast.loading('Merging documents...', { id: 'merge-docs' });
      const response = await axios.get(`${API}/task-manager/customer/order-documents/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      
      const mergedPdf = await PDFDocument.create();
      let hasDocuments = false;
      
      // Add Invoice
      if (data.tally_invoice) {
        try {
          let invoiceData = data.tally_invoice;
          if (invoiceData.includes(',')) {
            invoiceData = invoiceData.split(',')[1];
          }
          const invoiceBytes = Uint8Array.from(atob(invoiceData), c => c.charCodeAt(0));
          const invoicePdf = await PDFDocument.load(invoiceBytes);
          const pages = await mergedPdf.copyPages(invoicePdf, invoicePdf.getPageIndices());
          pages.forEach(page => mergedPdf.addPage(page));
          hasDocuments = true;
        } catch (e) {
          console.error('Invoice merge error:', e);
        }
      }
      
      // Add Packing List
      if (data.packing_list?.length > 0) {
        try {
          const packingDoc = new jsPDF();
          packingDoc.setFontSize(14);
          packingDoc.setTextColor(0, 102, 204);
          packingDoc.text('PACKING LIST', 105, 15, { align: 'center' });
          packingDoc.setFontSize(10);
          packingDoc.setTextColor(0, 0, 0);
          packingDoc.text(`Order: ${data.order_id}`, 14, 28);
          packingDoc.text(`Customer: ${data.customer_name}`, 14, 34);
          
          autoTable(packingDoc, {
            startY: 45,
            head: [['Part No', 'Description', 'Ordered', 'Packed', 'Cartons (Weight)']],
            body: data.packing_list.map(item => [
              item.jp_part_no,
              item.part_name || '-',
              item.order_qty || '-',
              item.packed_qty || '-',
              item.cartons?.map(c => {
                const wk = Number(c.weight_kg || 0);
                const num = c.carton_number || c.carton_no;
                return wk > 0 ? `${num} (${wk.toFixed(1)}kg)` : `${num}`;
              }).join(', ') || '-'
            ]),
            theme: 'grid',
            headStyles: { fillColor: [0, 102, 204] },
            styles: { fontSize: 9 }
          });
          const _twk_pl = Number(data.total_weight_kg || 0);
          if (_twk_pl > 0) {
            const _fY_pl = packingDoc.lastAutoTable ? packingDoc.lastAutoTable.finalY : 60;
            packingDoc.setFontSize(9);
            packingDoc.setFont(undefined, 'bold');
            packingDoc.text(`Total Weight: ${_twk_pl.toFixed(2)} kg`, 196, _fY_pl + 6, { align: 'right' });
            packingDoc.setFont(undefined, 'normal');
          }
          
          const packingPdfBytes = packingDoc.output('arraybuffer');
          const packingPdf = await PDFDocument.load(packingPdfBytes);
          const packingPages = await mergedPdf.copyPages(packingPdf, packingPdf.getPageIndices());
          packingPages.forEach(page => mergedPdf.addPage(page));
          hasDocuments = true;
        } catch (e) {
          console.error('Packing list merge error:', e);
        }
      }
      
      // Add LR (can be PDF or image)
      if (data.lr_details?.lr_image) {
        const lrDataUrl = data.lr_details.lr_image;
        const isPdfLR = lrDataUrl.startsWith('data:application/pdf');
        
        if (isPdfLR) {
          // LR is PDF - merge directly
          try {
            let lrData = lrDataUrl;
            if (lrData.includes(',')) {
              lrData = lrData.split(',')[1];
            }
            const lrBytes = Uint8Array.from(atob(lrData), c => c.charCodeAt(0));
            const lrPdf = await PDFDocument.load(lrBytes);
            const pages = await mergedPdf.copyPages(lrPdf, lrPdf.getPageIndices());
            pages.forEach(page => mergedPdf.addPage(page));
            hasDocuments = true;
          } catch (e) {
            console.error('LR PDF merge error:', e);
          }
        } else {
          // LR is image - convert to PDF page
          try {
            let lrData = lrDataUrl;
            if (lrData.includes(',')) {
              lrData = lrData.split(',')[1];
            }
            const lrBytes = Uint8Array.from(atob(lrData), c => c.charCodeAt(0));
            
            const page = mergedPdf.addPage();
            const { width, height } = page.getSize();
            
            let image;
            if (lrDataUrl.startsWith('data:image/png')) {
              image = await mergedPdf.embedPng(lrBytes);
            } else {
              image = await mergedPdf.embedJpg(lrBytes);
            }
            
            // Scale image to fit page while maintaining aspect ratio
            const imgDims = image.scale(1);
            const scale = Math.min(width / imgDims.width, height / imgDims.height) * 0.9;
            
            page.drawImage(image, {
              x: (width - imgDims.width * scale) / 2,
              y: (height - imgDims.height * scale) / 2,
              width: imgDims.width * scale,
              height: imgDims.height * scale,
            });
            hasDocuments = true;
          } catch (e) {
            console.error('LR image merge error:', e);
          }
        }
      }
      
      toast.dismiss('merge-docs');
      
      if (!hasDocuments || mergedPdf.getPageCount() === 0) {
        toast.error('No documents available');
        setDocViewerOpen(false);
        return;
      }
      
      const mergedBytes = await mergedPdf.save();
      // Convert to Blob URL for better browser compatibility with large PDFs
      const blob = new Blob([mergedBytes], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      setDocViewerUrl(blobUrl);
    } catch (error) {
      toast.dismiss('merge-docs');
      console.error('View all error:', error);
      toast.error('Failed to load documents');
      setDocViewerOpen(false);
    } finally {
      setDocViewerLoading(false);
    }
  };

  // Print All Documents - merged PDF (uses admin-grade server renderer)
  const printAllDocuments = async (orderId) => {
    toast.loading('Preparing documents...', { id: 'print-all' });
    try {
      const pdfData = await fetchMergedDocsPdf(orderId);
      toast.dismiss('print-all');
      // printDocument already handles a data:application/pdf;base64,... URL
      printDocument(pdfData);
    } catch (error) {
      toast.dismiss('print-all');
      console.error('Print all error:', error);
      const msg = error?.response?.data?.detail || error?.message || 'Failed to print documents';
      toast.error(msg);
    }
  };

  /* Legacy client-side printAllDocuments preserved as reference. */
  const printAllDocumentsLegacy = async (orderId) => {
    toast.loading('Preparing documents...', { id: 'print-all' });
    try {
      const response = await axios.get(`${API}/task-manager/customer/order-documents/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = response.data;
      
      const mergedPdf = await PDFDocument.create();
      let hasDocuments = false;
      
      // Add Invoice
      if (data.tally_invoice) {
        try {
          let invoiceData = data.tally_invoice;
          if (invoiceData.includes(',')) {
            invoiceData = invoiceData.split(',')[1];
          }
          const invoiceBytes = Uint8Array.from(atob(invoiceData), c => c.charCodeAt(0));
          const invoicePdf = await PDFDocument.load(invoiceBytes);
          const pages = await mergedPdf.copyPages(invoicePdf, invoicePdf.getPageIndices());
          pages.forEach(page => mergedPdf.addPage(page));
          hasDocuments = true;
        } catch (e) {
          console.error('Invoice merge error:', e);
        }
      }
      
      // Add Packing List
      if (data.packing_list?.length > 0) {
        try {
          const packingDoc = new jsPDF();
          packingDoc.setFontSize(14);
          packingDoc.setTextColor(0, 102, 204);
          packingDoc.text('PACKING LIST', 105, 15, { align: 'center' });
          packingDoc.setFontSize(10);
          packingDoc.setTextColor(0, 0, 0);
          packingDoc.text(`Order: ${data.order_id}`, 14, 28);
          packingDoc.text(`Customer: ${data.customer_name}`, 14, 34);
          
          autoTable(packingDoc, {
            startY: 45,
            head: [['Part No', 'Description', 'Ordered', 'Packed', 'Cartons (Weight)']],
            body: data.packing_list.map(item => [
              item.jp_part_no,
              item.part_name || '-',
              item.order_qty || '-',
              item.packed_qty || '-',
              item.cartons?.map(c => {
                const wk = Number(c.weight_kg || 0);
                const num = c.carton_number || c.carton_no;
                return wk > 0 ? `${num} (${wk.toFixed(1)}kg)` : `${num}`;
              }).join(', ') || '-'
            ]),
            theme: 'grid',
            headStyles: { fillColor: [0, 102, 204] },
            styles: { fontSize: 9 }
          });
          const _twk_pl = Number(data.total_weight_kg || 0);
          if (_twk_pl > 0) {
            const _fY_pl = packingDoc.lastAutoTable ? packingDoc.lastAutoTable.finalY : 60;
            packingDoc.setFontSize(9);
            packingDoc.setFont(undefined, 'bold');
            packingDoc.text(`Total Weight: ${_twk_pl.toFixed(2)} kg`, 196, _fY_pl + 6, { align: 'right' });
            packingDoc.setFont(undefined, 'normal');
          }
          
          const packingPdfBytes = packingDoc.output('arraybuffer');
          const packingPdf = await PDFDocument.load(packingPdfBytes);
          const packingPages = await mergedPdf.copyPages(packingPdf, packingPdf.getPageIndices());
          packingPages.forEach(page => mergedPdf.addPage(page));
          hasDocuments = true;
        } catch (e) {
          console.error('Packing list merge error:', e);
        }
      }
      
      // Add LR (can be PDF or image)
      if (data.lr_details?.lr_image) {
        const lrDataUrl = data.lr_details.lr_image;
        const isPdfLR = lrDataUrl.startsWith('data:application/pdf');
        
        if (isPdfLR) {
          // LR is PDF - merge directly
          try {
            let lrData = lrDataUrl;
            if (lrData.includes(',')) {
              lrData = lrData.split(',')[1];
            }
            const lrBytes = Uint8Array.from(atob(lrData), c => c.charCodeAt(0));
            const lrPdf = await PDFDocument.load(lrBytes);
            const pages = await mergedPdf.copyPages(lrPdf, lrPdf.getPageIndices());
            pages.forEach(page => mergedPdf.addPage(page));
            hasDocuments = true;
          } catch (e) {
            console.error('LR PDF merge error:', e);
          }
        } else {
          // LR is image - convert to PDF page
          try {
            let lrData = lrDataUrl;
            if (lrData.includes(',')) {
              lrData = lrData.split(',')[1];
            }
            const lrBytes = Uint8Array.from(atob(lrData), c => c.charCodeAt(0));
            
            const page = mergedPdf.addPage();
            const { width, height } = page.getSize();
            
            let image;
            if (lrDataUrl.startsWith('data:image/png')) {
              image = await mergedPdf.embedPng(lrBytes);
            } else {
              image = await mergedPdf.embedJpg(lrBytes);
            }
            
            // Scale image to fit page while maintaining aspect ratio
            const imgDims = image.scale(1);
            const scale = Math.min(width / imgDims.width, height / imgDims.height) * 0.9;
            
            page.drawImage(image, {
              x: (width - imgDims.width * scale) / 2,
              y: (height - imgDims.height * scale) / 2,
              width: imgDims.width * scale,
              height: imgDims.height * scale,
            });
            hasDocuments = true;
          } catch (e) {
            console.error('LR image merge error:', e);
          }
        }
      }
      
      toast.dismiss('print-all');
      
      if (!hasDocuments || mergedPdf.getPageCount() === 0) {
        toast.error('No documents available');
        return;
      }
      
      const mergedBytes = await mergedPdf.save();
      const base64 = uint8ArrayToBase64(new Uint8Array(mergedBytes));
      printDocument(`data:application/pdf;base64,${base64}`);
    } catch (error) {
      toast.dismiss('print-all');
      console.error('Print all error:', error);
      toast.error('Failed to print documents');
    }
  };

  // Helper to detect file type from base64 data URL
  const getFileExtension = (base64Data) => {
    if (!base64Data) return 'png';
    if (base64Data.startsWith('data:application/pdf')) return 'pdf';
    if (base64Data.startsWith('data:image/jpeg')) return 'jpg';
    if (base64Data.startsWith('data:image/png')) return 'png';
    if (base64Data.startsWith('data:image/webp')) return 'webp';
    if (base64Data.startsWith('data:image/gif')) return 'gif';
    // Default to png for unknown types
    return 'png';
  };

  // Download individual Tally Invoice only
  const downloadTallyInvoice = async (orderId) => {
    try {
      const response = await axios.get(`${API}/task-manager/customer/order-documents/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      
      if (!data.tally_invoice) {
        toast.error('Tally Invoice not available for this order');
        return;
      }
      
      // Detect file type and use correct extension
      const extension = getFileExtension(data.tally_invoice);
      const link = document.createElement('a');
      link.href = data.tally_invoice;
      link.download = `TallyInvoice_${orderId}.${extension}`;
      link.click();
      toast.success('Tally Invoice downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download invoice');
    }
  };

  // Download Packing List only
  const downloadPackingList = async (orderId) => {
    try {
      const response = await axios.get(`${API}/task-manager/customer/order-documents/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      
      if (!data.packing_list || data.packing_list.length === 0) {
        toast.error('Packing List not available for this order');
        return;
      }
      
      const doc = new jsPDF();
      let currentY = 10;
      
      doc.setFontSize(14);
      doc.setTextColor(0, 102, 204);
      doc.text('PACKING LIST', 105, currentY, { align: 'center' });
      currentY += 10;
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Order: ${data.order_id}`, 14, currentY);
      doc.text(`Customer: ${data.customer_name}`, 14, currentY + 6);
      
      // Right side - Invoice and LR details
      doc.text(`Packed: ${formatDate(data.packing_finalized_at)}`, 120, currentY);
      let rightY = currentY + 6;
      if (data.tally_invoice_number) {
        doc.text(`Invoice #: ${data.tally_invoice_number}`, 120, rightY);
        rightY += 6;
      }
      if (data.lr_details?.lr_number) {
        doc.text(`LR #: ${data.lr_details.lr_number}`, 120, rightY);
        rightY += 6;
      }
      if (data.lr_details?.lr_date) {
        doc.text(`LR Date: ${data.lr_details.lr_date}`, 120, rightY);
      }
      
      currentY = Math.max(currentY + 12, rightY + 6);
      
      // Add packing table
      autoTable(doc, {
        startY: currentY,
        head: [['Part No', 'Description', 'Ordered', 'Packed', 'Carton#']],
        body: data.packing_list.map(item => [
          item.jp_part_no,
          item.part_name || '-',
          item.ordered_qty || '-',
          item.packed_qty || '-',
          item.carton_numbers?.join(', ') || '-'
        ]),
        theme: 'grid',
        headStyles: { fillColor: [0, 102, 204] },
        styles: { fontSize: 9 }
      });
      
      doc.save(`PackingList_${orderId}.pdf`);
      toast.success('Packing List downloaded');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download packing list');
    }
  };

  // Download LR Details only
  const downloadLRDetails = async (orderId) => {
    try {
      const response = await axios.get(`${API}/task-manager/customer/order-documents/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      
      if (!data.lr_details || !data.lr_details.lr_number) {
        toast.error('LR Details not available for this order');
        return;
      }
      
      // If there's an LR image, download it
      if (data.lr_details.lr_image) {
        const extension = getFileExtension(data.lr_details.lr_image);
        const link = document.createElement('a');
        link.href = data.lr_details.lr_image;
        link.download = `LR_${data.lr_details.lr_number}_${orderId}.${extension}`;
        link.click();
        toast.success('LR Copy downloaded');
      } else {
        // Generate a simple PDF with LR details
        const doc = new jsPDF();
        doc.setFontSize(14);
        doc.text('LR DETAILS', 105, 20, { align: 'center' });
        doc.setFontSize(10);
        doc.text(`Order: ${orderId}`, 20, 40);
        doc.text(`Transport: ${data.lr_details.transport_name}`, 20, 50);
        doc.text(`LR Number: ${data.lr_details.lr_number}`, 20, 60);
        doc.text(`LR Date: ${data.lr_details.lr_date || '-'}`, 20, 70);
        doc.save(`LR_${orderId}.pdf`);
        toast.success('LR Details downloaded');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download LR details');
    }
  };

  // View Invoice - opens in dialog for view/print
  const viewInvoice = async (orderId) => {
    setDocViewerLoading(true);
    setDocViewerType('invoice');
    setDocViewerOrderId(orderId);
    setDocViewerOpen(true);
    
    try {
      const response = await axios.get(`${API}/task-manager/customer/order-documents/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.tally_invoice) {
        // Convert to Blob URL for better browser compatibility
        let invoiceData = response.data.tally_invoice;
        let base64Data;
        if (invoiceData.includes(',')) {
          base64Data = invoiceData.split(',')[1];
        } else {
          base64Data = invoiceData;
        }
        const binaryData = atob(base64Data);
        const bytes = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          bytes[i] = binaryData.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        setDocViewerUrl(blobUrl);
      } else {
        toast.error('Invoice not available for this order');
        setDocViewerOpen(false);
      }
    } catch (error) {
      console.error('View error:', error);
      toast.error('Failed to load invoice');
      setDocViewerOpen(false);
    } finally {
      setDocViewerLoading(false);
    }
  };

  // View Packing List - opens in dialog
  const viewPackingList = async (orderId) => {
    setDocViewerLoading(true);
    setDocViewerType('packing');
    setDocViewerOrderId(orderId);
    setDocViewerOpen(true);
    
    try {
      const response = await axios.get(`${API}/task-manager/customer/order-documents/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const data = response.data;
      
      if (!data.packing_list || data.packing_list.length === 0) {
        toast.error('Packing List not available for this order');
        setDocViewerOpen(false);
        return;
      }
      
      // Generate PDF
      const doc = new jsPDF();
      let currentY = 10;
      
      doc.setFontSize(14);
      doc.setTextColor(0, 102, 204);
      doc.text('PACKING LIST', 105, currentY, { align: 'center' });
      currentY += 10;
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Order: ${data.order_id}`, 14, currentY);
      doc.text(`Customer: ${data.customer_name}`, 14, currentY + 6);
      
      doc.text(`Packed: ${formatDate(data.packing_finalized_at)}`, 120, currentY);
      let rightY = currentY + 6;
      if (data.tally_invoice_number) {
        doc.text(`Invoice #: ${data.tally_invoice_number}`, 120, rightY);
        rightY += 6;
      }
      if (data.lr_details?.lr_number) {
        doc.text(`LR #: ${data.lr_details.lr_number}`, 120, rightY);
        rightY += 6;
      }
      if (data.lr_details?.lr_date) {
        doc.text(`LR Date: ${data.lr_details.lr_date}`, 120, rightY);
      }
      
      currentY = Math.max(currentY + 12, rightY + 6);
      
      autoTable(doc, {
        startY: currentY,
        head: [['Part No', 'Description', 'Ordered', 'Packed', 'Carton#']],
        body: data.packing_list.map(item => [
          item.jp_part_no,
          item.part_name || '-',
          item.order_qty || '-',
          item.packed_qty || '-',
          item.cartons?.map(c => c.carton_number).join(', ') || '-'
        ]),
        theme: 'grid',
        headStyles: { fillColor: [0, 102, 204] },
        styles: { fontSize: 9 }
      });
      
      // Convert to Blob URL for better browser compatibility
      const pdfBytes = doc.output('arraybuffer');
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const blobUrl = URL.createObjectURL(blob);
      setDocViewerUrl(blobUrl);
    } catch (error) {
      console.error('View error:', error);
      toast.error('Failed to load packing list');
      setDocViewerOpen(false);
    } finally {
      setDocViewerLoading(false);
    }
  };

  // View LR - opens in dialog
  const viewLR = async (orderId) => {
    setDocViewerLoading(true);
    setDocViewerType('lr');
    setDocViewerOrderId(orderId);
    setDocViewerOpen(true);
    
    try {
      const response = await axios.get(`${API}/task-manager/customer/order-documents/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.lr_details?.lr_image) {
        // Convert to Blob URL for better browser compatibility
        let lrData = response.data.lr_details.lr_image;
        let base64Data;
        let mimeType = 'application/pdf';
        
        if (lrData.includes(',')) {
          const parts = lrData.split(',');
          base64Data = parts[1];
          // Extract mime type from data URL
          if (parts[0].includes('image/')) {
            mimeType = parts[0].match(/data:([^;]+)/)[1];
          }
        } else {
          base64Data = lrData;
        }
        
        const binaryData = atob(base64Data);
        const bytes = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          bytes[i] = binaryData.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);
        setDocViewerUrl(blobUrl);
      } else {
        toast.error('LR not available for this order');
        setDocViewerOpen(false);
      }
    } catch (error) {
      console.error('View error:', error);
      toast.error('Failed to load LR');
      setDocViewerOpen(false);
    } finally {
      setDocViewerLoading(false);
    }
  };

  // Print document
  const printDocument = (url) => {
    if (!url) {
      toast.error('Document not available');
      return;
    }
    
    // For Blob URLs (PDF), open directly
    if (url.startsWith('blob:')) {
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      }
      return;
    }
    
    // For PDF data URLs, create a blob and open in new window
    if (url.startsWith('data:application/pdf')) {
      try {
        const base64Data = url.split(',')[1];
        const binaryData = atob(base64Data);
        const bytes = new Uint8Array(binaryData.length);
        for (let i = 0; i < binaryData.length; i++) {
          bytes[i] = binaryData.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const blobUrl = URL.createObjectURL(blob);
        
        const printWindow = window.open(blobUrl, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            setTimeout(() => {
              printWindow.print();
            }, 500);
          };
        }
      } catch (e) {
        console.error('Print error:', e);
        toast.error('Failed to print document');
      }
    } else if (url.startsWith('data:image')) {
      // For images, create a printable page
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head><title>Print</title></head>
            <body style="margin:0;padding:20px;text-align:center;">
              <img src="${url}" style="max-width:100%;max-height:100vh;" onload="window.print();" />
            </body>
          </html>
        `);
        printWindow.document.close();
      }
    } else {
      // Regular URL
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
    }
  };

  // Print document by fetching URL first (for dropdown) - handles all doc types
  const printDocDirect = async (orderId, type) => {
    try {
      toast.loading('Loading document...', { id: 'print-load' });
      
      if (type === 'packing') {
        // Generate packing list PDF and print
        const response = await axios.get(`${API}/task-manager/customer/order-documents/${orderId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = response.data;
        
        if (!data.packing_list || data.packing_list.length === 0) {
          toast.dismiss('print-load');
          toast.error('Packing List not available');
          return;
        }
        
        const doc = new jsPDF();
        let currentY = 10;
        
        doc.setFontSize(14);
        doc.setTextColor(0, 102, 204);
        doc.text('PACKING LIST', 105, currentY, { align: 'center' });
        currentY += 10;
        
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        doc.text(`Order: ${data.order_id}`, 14, currentY);
        doc.text(`Customer: ${data.customer_name}`, 14, currentY + 6);
        doc.text(`Packed: ${formatDate(data.packing_finalized_at)}`, 120, currentY);
        
        let rightY = currentY + 6;
        if (data.tally_invoice_number) {
          doc.text(`Invoice #: ${data.tally_invoice_number}`, 120, rightY);
          rightY += 6;
        }
        if (data.lr_details?.lr_number) {
          doc.text(`LR #: ${data.lr_details.lr_number}`, 120, rightY);
        }
        
        currentY = Math.max(currentY + 12, rightY + 6);
        
        autoTable(doc, {
          startY: currentY,
          head: [['Part No', 'Description', 'Ordered', 'Packed', 'Carton#']],
          body: data.packing_list.map(item => [
            item.jp_part_no,
            item.part_name || '-',
            item.order_qty || '-',
            item.packed_qty || '-',
            item.cartons?.map(c => c.carton_number).join(', ') || '-'
          ]),
          theme: 'grid',
          headStyles: { fillColor: [0, 102, 204] },
          styles: { fontSize: 9 }
        });
        
        toast.dismiss('print-load');
        const pdfDataUrl = doc.output('datauristring');
        printDocument(pdfDataUrl);
        return;
      }
      
      // Invoice or LR
      const response = await axios.get(`${API}/task-manager/customer/order-documents/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.dismiss('print-load');
      
      let url;
      if (type === 'invoice') {
        url = response.data.tally_invoice;
      } else if (type === 'lr') {
        url = response.data.lr_details?.lr_image;
      }
      
      if (!url) {
        toast.error(`${type === 'invoice' ? 'Invoice' : 'LR'} not available`);
        return;
      }
      
      // Ensure proper data URL format
      if (!url.startsWith('data:')) {
        url = `data:application/pdf;base64,${url}`;
      }
      
      printDocument(url);
    } catch (err) {
      toast.dismiss('print-load');
      console.error('Print error:', err);
      toast.error('Failed to load document for printing');
    }
  };

  // Download document
  const downloadDocument = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    toast.success('Document downloaded');
  };

  // Upload LR
  const uploadLR = async () => {
    if (!lrFormData.lr_number || !lrFormData.transport_name) {
      toast.error('Please fill in Transport Name and LR Number');
      return;
    }
    
    setLrUploading(true);
    try {
      const payload = {
        order_id: lrUploadOrderId,
        transport_name: lrFormData.transport_name,
        lr_number: lrFormData.lr_number,
        lr_date: lrFormData.lr_date || new Date().toISOString().split('T')[0],
        lr_image: lrFormData.lr_image || null  // base64 string
      };
      
      await axios.post(
        `${API}/task-manager/customer/order/${lrUploadOrderId}/upload-lr`,
        payload,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      
      toast.success('LR details uploaded successfully');
      setLrUploadOpen(false);
      setLrFormData({ transport_name: '', lr_number: '', lr_date: '', lr_image: null });
      loadOrders(page);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload LR');
    } finally {
      setLrUploading(false);
    }
  };

  // Handle LR file selection - convert to base64
  const handleLrFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('application/pdf') && !file.type.startsWith('image/')) {
      toast.error('Please upload a PDF or image file');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setLrFormData(prev => ({ ...prev, lr_image: reader.result }));
      toast.success('File selected');
    };
    reader.readAsDataURL(file);
  };

  // Use device's local time - no timezone conversion needed
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-amber-50 text-amber-700 border border-amber-200', icon: Clock, label: 'Pending' },
      'confirmed': { color: 'bg-blue-50 text-blue-700 border border-blue-200', icon: CheckCircle, label: 'Confirmed' },
      'processing': { color: 'bg-violet-50 text-violet-700 border border-violet-200', icon: Package, label: 'Processing' },
      'shipped': { color: 'bg-indigo-50 text-indigo-700 border border-indigo-200', icon: Truck, label: 'Shipped' },
      'dispatched': { color: 'bg-purple-50 text-purple-700 border border-purple-200', icon: Truck, label: 'Dispatched' },
      'delivered': { color: 'bg-emerald-50 text-emerald-700 border border-emerald-200', icon: CheckCircle, label: 'Delivered' },
      'cancelled': { color: 'bg-red-50 text-red-700 border border-red-200', icon: XCircle, label: 'Cancelled' },
      'closed': { color: 'bg-gray-100 text-gray-700 border border-gray-300', icon: CheckCircle, label: 'Closed' }
    };
    
    const config = statusConfig[status?.toLowerCase()] || statusConfig['pending'];
    const Icon = config.icon;
    
    return (
      <Badge className={`${config.color} flex items-center gap-1.5 px-2.5 py-1 font-medium`}>
        <Icon className="w-3.5 h-3.5" />
        {config.label}
      </Badge>
    );
  };

  // Calculate basic price (price before 18% GST)
  const calculateBasicPrice = (taxPaidPrice) => {
    return (taxPaidPrice || 0) / 1.18;
  };

  // Generate filename from company name and date
  const generateFileName = (order, extension) => {
    const companyName = (order.company_name || order.customer_name || 'Order').replace(/[^a-zA-Z0-9\s]/g, '').trim();
    const orderDate = new Date(order.created_at).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit', 
      year: 'numeric'
    }).replace(/\//g, '-');
    return `${companyName} - ${orderDate}.${extension}`;
  };

  // Share file on mobile or download on desktop
  const shareOrDownload = async (blob, fileName, mimeType) => {
    if (navigator.share && navigator.canShare) {
      const file = new File([blob], fileName, { type: mimeType });
      const shareData = { files: [file] };
      
      if (navigator.canShare(shareData)) {
        try {
          await navigator.share(shareData);
          toast.success('Share dialog opened');
          return;
        } catch (err) {
          if (err.name !== 'AbortError') {
            console.log('Share failed, falling back to download');
          } else {
            return;
          }
        }
      }
    }
    
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success('File downloaded successfully');
  };

  // Export order to PDF - matching Excel format
  const exportOrderToPDF = async (order) => {
    try {
      const doc = new jsPDF();
      const isDispatchedOrClosed = ['dispatched', 'closed', 'delivered'].includes(order.status?.toLowerCase());
      
      // Fetch packing data for dispatched orders
      let packingData = {};
      if (isDispatchedOrClosed) {
        try {
          const response = await axios.get(`${API}/task-manager/customer/order-documents/${order.order_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Create a map of part_no -> packing info with detailed carton breakdown
          (response.data.packing_list || []).forEach(item => {
            // Format each carton as "CARTON NO X - Y PC (Z kg)"
            const cartonDetails = item.cartons?.map(c => {
              const cartonNo = c.carton_number || c.carton_no;
              const qty = c.packed_qty || 0;
              const wk = Number(c.weight_kg || 0);
              return wk > 0
                ? `CTN ${cartonNo} - ${qty} PC (${wk.toFixed(1)}kg)`
                : `CTN ${cartonNo} - ${qty} PC`;
            }).join('\n') || '-';
            
            packingData[item.jp_part_no] = {
              packed_qty: item.packed_qty || 0,
              cartonDetails: cartonDetails
            };
          });
          packingData.__totalWeightKg = Number(response.data.total_weight_kg || 0);
        } catch (e) {
          console.error('Failed to fetch packing data:', e);
        }
      }
      
      // Header
      doc.setFontSize(18);
      doc.setTextColor(220, 38, 38);
      doc.text('JAI PARAS SPARES', 105, 15, { align: 'center' });
      
      // Order info - Left side
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Order: ${order.order_id}`, 14, 28);
      doc.text(`Customer: ${order.customer_name || '-'}`, 14, 34);
      doc.text(`Company: ${order.company_name || '-'}`, 14, 40);
      
      // Order info - Right side
      doc.text(`Date: ${new Date(order.created_at).toLocaleDateString('en-IN')}`, 120, 28);
      
      // Add Tally Invoice #, LR #, LR Date for dispatched orders
      let rightY = 34;
      if (order.tally_invoice_number) {
        doc.text(`Invoice #: ${order.tally_invoice_number}`, 120, rightY);
        rightY += 6;
      }
      if (order.lr_details?.lr_number) {
        doc.text(`LR #: ${order.lr_details.lr_number}`, 120, rightY);
        rightY += 6;
      }
      if (order.lr_details?.lr_date) {
        doc.text(`LR Date: ${order.lr_details.lr_date}`, 120, rightY);
        rightY += 6;
      }
      // Discount percentage intentionally NOT shown on shared PDF (sales secret).
      
      // Table headers and data - add Packing column for dispatched orders
      const headers = isDispatchedOrClosed 
        ? [['S No', 'Jp Part No', 'Part Name', 'Qty', 'Price', 'Amount', 'Packing']]
        : [['S No', 'Jp Part No', 'Part Name', 'Quantity', 'Price', 'Amount']];
      
      const tableData = order.items.map((item, index) => {
        const taxPaidUnitPrice = item.discounted_price || item.price || item.mrp || 0;
        const basicPricePerUnit = calculateBasicPrice(taxPaidUnitPrice);
        const basicAmount = basicPricePerUnit * item.quantity;
        
        const row = [
          (index + 1).toString(),
          item.jp_part_no,
          item.part_name.length > 30 ? item.part_name.substring(0, 30) + '...' : item.part_name,
          item.quantity.toString(),
          basicPricePerUnit.toFixed(2),
          basicAmount.toFixed(2)
        ];
        
        // Add packing info for dispatched orders
        if (isDispatchedOrClosed) {
          const packing = packingData[item.jp_part_no];
          if (packing && packing.cartonDetails && packing.cartonDetails !== '-') {
            row.push(packing.cartonDetails);
          } else if (packing && packing.packed_qty > 0) {
            row.push(`${packing.packed_qty} PC`);
          } else {
            row.push('-');
          }
        }
        
        return row;
      });

      // Calculate totals
      const totalBasicAmount = order.items.reduce((sum, item) => {
        const taxPaidUnitPrice = item.discounted_price || item.price || item.mrp || 0;
        const basicPricePerUnit = calculateBasicPrice(taxPaidUnitPrice);
        return sum + (basicPricePerUnit * item.quantity);
      }, 0);
      
      const gstAmount = totalBasicAmount * 0.18;
      const totalAmount = totalBasicAmount + gstAmount;

      // Generate PDF table - adjust startY based on how much header info we have
      const tableStartY = Math.max(48, rightY + 6);
      
      // Column widths depend on whether we have packing column
      const columnStyles = isDispatchedOrClosed ? {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 24 },
        2: { cellWidth: 50 },
        3: { cellWidth: 12, halign: 'center' },
        4: { cellWidth: 20, halign: 'right' },
        5: { cellWidth: 22, halign: 'right' },
        6: { cellWidth: 40 }  // Packing column
      } : {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 28 },
        2: { cellWidth: 70 },
        3: { cellWidth: 18, halign: 'center' },
        4: { cellWidth: 25, halign: 'right' },
        5: { cellWidth: 28, halign: 'right' }
      };
      
      autoTable(doc, {
        startY: tableStartY,
        head: headers,
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [220, 38, 38], fontSize: 8, fontStyle: 'bold', halign: 'center' },
        bodyStyles: { fontSize: 7 },
        columnStyles: columnStyles,
        margin: { left: 14, right: 14 }
      });

      // Footer totals
      const finalY = doc.lastAutoTable.finalY + 5;
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text('Total Basic Amount:', 120, finalY);
      doc.text(totalBasicAmount.toFixed(2), 182, finalY, { align: 'right' });
      
      doc.text('GST (18%):', 120, finalY + 6);
      doc.text(gstAmount.toFixed(2), 182, finalY + 6, { align: 'right' });
      
      doc.setDrawColor(0);
      doc.line(120, finalY + 9, 182, finalY + 9);
      
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('TOTAL AMOUNT:', 120, finalY + 15);
      doc.text(totalAmount.toFixed(2), 182, finalY + 15, { align: 'right' });

      // Generate file as blob for sharing
      const fileName = generateFileName(order, 'pdf');
      const pdfBlob = doc.output('blob');
      
      await shareOrDownload(pdfBlob, fileName, 'application/pdf');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };

  // Print order directly
  const printOrder = async (order) => {
    try {
      const doc = new jsPDF();
      const isDispatchedOrClosed = ['dispatched', 'closed', 'delivered'].includes(order.status?.toLowerCase());
      
      // Fetch packing data for dispatched orders
      let packingData = {};
      if (isDispatchedOrClosed) {
        try {
          const response = await axios.get(`${API}/task-manager/customer/order-documents/${order.order_id}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          // Create a map of part_no -> packing info with detailed carton breakdown
          (response.data.packing_list || []).forEach(item => {
            // Format each carton as "CARTON NO X - Y PC (Z kg)"
            const cartonDetails = item.cartons?.map(c => {
              const cartonNo = c.carton_number || c.carton_no;
              const qty = c.packed_qty || 0;
              const wk = Number(c.weight_kg || 0);
              return wk > 0
                ? `CTN ${cartonNo} - ${qty} PC (${wk.toFixed(1)}kg)`
                : `CTN ${cartonNo} - ${qty} PC`;
            }).join('\n') || '-';
            
            packingData[item.jp_part_no] = {
              packed_qty: item.packed_qty || 0,
              cartonDetails: cartonDetails
            };
          });
          packingData.__totalWeightKg = Number(response.data.total_weight_kg || 0);
        } catch (e) {
          console.error('Failed to fetch packing data:', e);
        }
      }
      
      // Header
      doc.setFontSize(18);
      doc.setTextColor(220, 38, 38);
      doc.text('JAI PARAS SPARES', 105, 15, { align: 'center' });
      
      // Order info - Left side
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text(`Order: ${order.order_id}`, 14, 28);
      doc.text(`Customer: ${order.customer_name || '-'}`, 14, 34);
      doc.text(`Company: ${order.company_name || '-'}`, 14, 40);
      
      // Order info - Right side
      doc.text(`Date: ${new Date(order.created_at).toLocaleDateString('en-IN')}`, 120, 28);
      
      // Add Tally Invoice #, LR #, LR Date for dispatched orders
      let rightY = 34;
      if (order.tally_invoice_number) {
        doc.text(`Invoice #: ${order.tally_invoice_number}`, 120, rightY);
        rightY += 6;
      }
      if (order.lr_details?.lr_number) {
        doc.text(`LR #: ${order.lr_details.lr_number}`, 120, rightY);
        rightY += 6;
      }
      if (order.lr_details?.lr_date) {
        doc.text(`LR Date: ${order.lr_details.lr_date}`, 120, rightY);
        rightY += 6;
      }
      // Discount percentage intentionally NOT shown on shared PDF (sales secret).
      
      // Table headers and data - add Packing column for dispatched orders
      const headers = isDispatchedOrClosed 
        ? [['S No', 'Jp Part No', 'Part Name', 'Qty', 'Price', 'Amount', 'Packing']]
        : [['S No', 'Jp Part No', 'Part Name', 'Quantity', 'Price', 'Amount']];
      
      const tableData = order.items.map((item, index) => {
        const taxPaidUnitPrice = item.discounted_price || item.price || item.mrp || 0;
        const basicPricePerUnit = calculateBasicPrice(taxPaidUnitPrice);
        const basicAmount = basicPricePerUnit * item.quantity;
        
        const row = [
          (index + 1).toString(),
          item.jp_part_no,
          item.part_name.length > 30 ? item.part_name.substring(0, 30) + '...' : item.part_name,
          item.quantity.toString(),
          basicPricePerUnit.toFixed(2),
          basicAmount.toFixed(2)
        ];
        
        // Add packing info for dispatched orders
        if (isDispatchedOrClosed) {
          const packing = packingData[item.jp_part_no];
          if (packing && packing.cartonDetails && packing.cartonDetails !== '-') {
            row.push(packing.cartonDetails);
          } else if (packing && packing.packed_qty > 0) {
            row.push(`${packing.packed_qty} PC`);
          } else {
            row.push('-');
          }
        }
        
        return row;
      });

      // Calculate totals
      const totalBasicAmount = order.items.reduce((sum, item) => {
        const taxPaidUnitPrice = item.discounted_price || item.price || item.mrp || 0;
        const basicPricePerUnit = calculateBasicPrice(taxPaidUnitPrice);
        return sum + (basicPricePerUnit * item.quantity);
      }, 0);
      
      const gstAmount = totalBasicAmount * 0.18;
      const totalAmount = totalBasicAmount + gstAmount;

      // Generate PDF table - adjust startY based on how much header info we have
      const tableStartY = Math.max(48, rightY + 6);
      
      // Column widths depend on whether we have packing column
      const columnStyles = isDispatchedOrClosed ? {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 24 },
        2: { cellWidth: 50 },
        3: { cellWidth: 12, halign: 'center' },
        4: { cellWidth: 20, halign: 'right' },
        5: { cellWidth: 22, halign: 'right' },
        6: { cellWidth: 40 }  // Packing column
      } : {
        0: { cellWidth: 12, halign: 'center' },
        1: { cellWidth: 28 },
        2: { cellWidth: 70 },
        3: { cellWidth: 18, halign: 'center' },
        4: { cellWidth: 25, halign: 'right' },
        5: { cellWidth: 28, halign: 'right' }
      };
      
      autoTable(doc, {
        startY: tableStartY,
        head: headers,
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [220, 38, 38], fontSize: 8, fontStyle: 'bold', halign: 'center' },
        bodyStyles: { fontSize: 7 },
        columnStyles: columnStyles,
        margin: { left: 14, right: 14 }
      });

      // Footer totals
      const finalY = doc.lastAutoTable.finalY + 5;
      
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.text('Total Basic Amount:', 120, finalY);
      doc.text(totalBasicAmount.toFixed(2), 182, finalY, { align: 'right' });
      
      doc.text('GST (18%):', 120, finalY + 6);
      doc.text(gstAmount.toFixed(2), 182, finalY + 6, { align: 'right' });
      
      doc.setDrawColor(0);
      doc.line(120, finalY + 9, 182, finalY + 9);
      
      doc.setFontSize(11);
      doc.setFont(undefined, 'bold');
      doc.text('TOTAL AMOUNT:', 120, finalY + 15);
      doc.text(totalAmount.toFixed(2), 182, finalY + 15, { align: 'right' });

      // Auto-print: Open in new window and trigger print
      const pdfBlob = doc.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl, '_blank');
      
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      } else {
        toast.error('Please allow pop-ups to print');
      }
    } catch (error) {
      console.error('Print error:', error);
      toast.error('Failed to print order');
    }
  };

  // Filter orders by status and sort by date (newest first)
  const pendingOrders = orders
    .filter(o => ['pending', 'confirmed', 'processing'].includes(o.status?.toLowerCase()))
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  const dispatchedOrders = orders
    .filter(o => ['shipped', 'dispatched', 'delivered', 'closed'].includes(o.status?.toLowerCase()))
    .sort((a, b) => new Date(b.dispatched_at || b.created_at) - new Date(a.dispatched_at || a.created_at));

  const renderOrderTable = (orderList, isPending) => (
    <Table>
      <TableHeader>
        <TableRow className="bg-gray-50">
          <TableHead className="text-gray-700 font-semibold">Order ID</TableHead>
          <TableHead className="text-gray-700 font-semibold">Date</TableHead>
          <TableHead className="text-gray-700 font-semibold">Items</TableHead>
          <TableHead className="text-gray-700 font-semibold">Total</TableHead>
          <TableHead className="text-gray-700 font-semibold">Status</TableHead>
          {!isPending && <TableHead className="text-gray-700 font-semibold">Dispatch Info</TableHead>}
          <TableHead className="text-gray-700 font-semibold text-right">Action</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {orderList.map((order) => (
          <TableRow key={order.order_id} className="border-b border-gray-100 hover:bg-gray-50">
            <TableCell>
              <span className="font-mono font-medium text-gray-900">{order.order_id}</span>
            </TableCell>
            <TableCell className="text-sm text-gray-700">
              {formatDate(order.created_at)}
              {order.placed_by_tag && (
                <div className="text-xs text-gray-500 mt-0.5">by {order.placed_by_tag}</div>
              )}
              {order.modified_by_tag && (
                <div className="text-xs text-orange-500 mt-0.5">Modified by {order.modified_by_tag}</div>
              )}
            </TableCell>
            <TableCell>
              <span className="text-gray-700">{order.items?.length || 0} items</span>
            </TableCell>
            <TableCell>
              <span className="font-semibold text-gray-900">
                {formatCurrency(order.items?.reduce((sum, item) => {
                  const dp = item.discounted_price || item.price || item.mrp || 0;
                  return sum + (dp * (item.quantity || 0));
                }, 0) || order.final_amount || 0)}
              </span>
            </TableCell>
            <TableCell>
              {getStatusBadge(order.status)}
            </TableCell>
            {!isPending && (
              <TableCell>
                {(order.tally_invoice_number || order.lr_details?.transport_name || order.lr_details?.lr_number) ? (
                  <div className="text-xs">
                    {order.tally_invoice_number && (
                      <p><span className="text-gray-500">Invoice #:</span> <span className="font-medium">{order.tally_invoice_number}</span></p>
                    )}
                    {order.lr_details?.transport_name && (
                      <p><span className="text-gray-500">Transport:</span> {order.lr_details.transport_name}</p>
                    )}
                    {order.lr_details?.lr_number && (
                      <p><span className="text-gray-500">LR:</span> {order.lr_details.lr_number}</p>
                    )}
                    {order.lr_details?.lr_date && (
                      <p><span className="text-gray-500">LR Date:</span> {order.lr_details.lr_date}</p>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-gray-400">-</span>
                )}
              </TableCell>
            )}
            <TableCell className="text-right">
              <div className="flex items-center justify-end gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => loadOrderDetails(order.order_id)}
                  title="View Order"
                >
                  <Eye className="w-4 h-4 mr-1" /> View
                </Button>
                {order.status === 'pending' && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setEditOrder(order)}
                    className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                    title="Edit Order"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => exportOrderToPDF(order)}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  title="Share/Download PDF"
                >
                  <FileDown className="w-4 h-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => printOrder(order)}
                  className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                  title="Print Order"
                >
                  <Printer className="w-4 h-4" />
                </Button>
                {/* Single Documents dropdown for dispatched/closed orders */}
                {['dispatched', 'closed', 'delivered'].includes(order.status?.toLowerCase()) && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-green-400 text-green-700 hover:bg-green-50"
                        data-testid={`documents-btn-${order.order_id}`}
                      >
                        <FileText className="w-4 h-4 mr-1" /> Documents <ChevronDown className="w-3 h-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-52">
                      {/* All Documents */}
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="cursor-pointer">
                          <ClipboardList className="w-4 h-4 mr-2 text-blue-600" /> All Documents
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="w-40">
                          <DropdownMenuItem onClick={() => viewAllDocuments(order.order_id)} className="cursor-pointer" data-testid={`view-all-${order.order_id}`}>
                            <Eye className="w-4 h-4 mr-2 text-blue-600" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => printAllDocuments(order.order_id)} className="cursor-pointer" data-testid={`print-all-${order.order_id}`}>
                            <Printer className="w-4 h-4 mr-2 text-gray-600" /> Print
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadOrderDocuments(order.order_id)} className="cursor-pointer" data-testid={`download-all-${order.order_id}`}>
                            <Download className="w-4 h-4 mr-2 text-green-600" /> Download
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                      <DropdownMenuSeparator />
                      {/* Invoice */}
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="cursor-pointer">
                          <Receipt className="w-4 h-4 mr-2 text-red-600" /> Invoice
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="w-40">
                          <DropdownMenuItem onClick={() => viewInvoice(order.order_id)} className="cursor-pointer" data-testid={`view-invoice-${order.order_id}`}>
                            <Eye className="w-4 h-4 mr-2 text-blue-600" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => printDocDirect(order.order_id, 'invoice')} className="cursor-pointer" data-testid={`print-invoice-${order.order_id}`}>
                            <Printer className="w-4 h-4 mr-2 text-gray-600" /> Print
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadTallyInvoice(order.order_id)} className="cursor-pointer" data-testid={`download-invoice-${order.order_id}`}>
                            <Download className="w-4 h-4 mr-2 text-green-600" /> Download
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                      {/* Packing List */}
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="cursor-pointer">
                          <Box className="w-4 h-4 mr-2 text-amber-600" /> Packing List
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="w-40">
                          <DropdownMenuItem onClick={() => viewPackingList(order.order_id)} className="cursor-pointer" data-testid={`view-packing-${order.order_id}`}>
                            <Eye className="w-4 h-4 mr-2 text-blue-600" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => printDocDirect(order.order_id, 'packing')} className="cursor-pointer" data-testid={`print-packing-${order.order_id}`}>
                            <Printer className="w-4 h-4 mr-2 text-gray-600" /> Print
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadPackingList(order.order_id)} className="cursor-pointer" data-testid={`download-packing-${order.order_id}`}>
                            <Download className="w-4 h-4 mr-2 text-green-600" /> Download
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                      {/* LR */}
                      <DropdownMenuSub>
                        <DropdownMenuSubTrigger className="cursor-pointer">
                          <Truck className="w-4 h-4 mr-2 text-purple-600" /> LR
                        </DropdownMenuSubTrigger>
                        <DropdownMenuSubContent className="w-40">
                          <DropdownMenuItem onClick={() => viewLR(order.order_id)} className="cursor-pointer" data-testid={`view-lr-${order.order_id}`}>
                            <Eye className="w-4 h-4 mr-2 text-blue-600" /> View
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => printDocDirect(order.order_id, 'lr')} className="cursor-pointer" data-testid={`print-lr-${order.order_id}`}>
                            <Printer className="w-4 h-4 mr-2 text-gray-600" /> Print
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => downloadLRDetails(order.order_id)} className="cursor-pointer" data-testid={`download-lr-${order.order_id}`}>
                            <Download className="w-4 h-4 mr-2 text-green-600" /> Download
                          </DropdownMenuItem>
                        </DropdownMenuSubContent>
                      </DropdownMenuSub>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );

  return (
    <div className="min-h-screen py-8 bg-gray-50 overflow-x-hidden">
      <div className="container mx-auto px-4 max-w-full">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4 min-w-0 flex-wrap">
            <Button variant="ghost" onClick={() => navigate(-1)} className="text-gray-700 flex-shrink-0">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">My Orders</h1>
            <Badge className="bg-gray-100 text-gray-800">{total} orders</Badge>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {unreadCount > 0 && (
              <Badge className="bg-red-500 text-white animate-pulse">
                <Bell className="w-3 h-3 mr-1" /> {unreadCount} new
              </Badge>
            )}
            <Button variant="outline" onClick={() => { loadOrders(page); loadNotifications(); }}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
          </div>
        </div>

        {/* Notifications Banner with Dismiss */}
        {notifications.length > 0 && notifications.filter(n => !n.is_read).length > 0 && (
          <Card className="mb-6 border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-blue-800 flex items-center gap-2">
                  <Bell className="w-4 h-4" /> Recent Updates
                </h3>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={dismissAllNotifications}
                  className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 text-xs"
                  data-testid="dismiss-all-notifications"
                >
                  <XCircle className="w-3 h-3 mr-1" /> Dismiss All
                </Button>
              </div>
              <div className="space-y-2">
                {notifications.filter(n => !n.is_read).slice(0, 5).map((notif) => (
                  <div key={notif.id || notif._id} className="text-sm text-blue-700 flex items-center justify-between gap-2 bg-white/50 rounded px-2 py-1">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                      <span>{notif.message}</span>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={() => dismissNotification(notif.id || notif._id)}
                      className="text-blue-400 hover:text-blue-600 p-1 h-auto"
                      data-testid={`dismiss-notification-${notif.id || notif._id}`}
                    >
                      <XCircle className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tabbed Orders View */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <TabsList className="bg-white border">
            <TabsTrigger value="pending" className="data-[state=active]:bg-amber-100 data-[state=active]:text-amber-800">
              <Clock className="w-4 h-4 mr-2" />
              Pending Orders
              {pendingOrders.length > 0 && (
                <Badge className="ml-2 bg-amber-500 text-white">{pendingOrders.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="dispatched" className="data-[state=active]:bg-green-100 data-[state=active]:text-green-800">
              <Truck className="w-4 h-4 mr-2" />
              Dispatched Orders
              {dispatchedOrders.length > 0 && (
                <Badge className="ml-2 bg-green-500 text-white">{dispatchedOrders.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Pending Orders Tab */}
          <TabsContent value="pending">
            <Card className="bg-white border border-gray-200">
              <CardContent className="p-0">
                {loading ? (
                  <div className="text-center py-16">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500">Loading orders...</p>
                  </div>
                ) : pendingOrders.length === 0 ? (
                  <div className="text-center py-16">
                    <CheckCircle className="w-16 h-16 mx-auto text-green-300 mb-4" />
                    <p className="text-gray-500 text-lg mb-2">No pending orders</p>
                    <p className="text-gray-400">All your orders have been dispatched!</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    {renderOrderTable(pendingOrders, true)}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Dispatched Orders Tab */}
          <TabsContent value="dispatched">
            <Card className="bg-white border border-gray-200">
              <CardContent className="p-0">
                {loading ? (
                  <div className="text-center py-16">
                    <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500">Loading orders...</p>
                  </div>
                ) : dispatchedOrders.length === 0 ? (
                  <div className="text-center py-16">
                    <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 text-lg mb-2">No dispatched orders yet</p>
                    <p className="text-gray-400">Orders will appear here after dispatch</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    {renderOrderTable(dispatchedOrders, false)}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-4 py-4 mt-4 bg-white rounded-lg border">
            <p className="text-sm text-gray-600">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => { loadOrders(page - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={page <= 1 || loading}
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { loadOrders(page + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                disabled={page >= totalPages || loading}
              >
                Next <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Order Details Dialog */}
        <Dialog open={orderDialogOpen} onOpenChange={setOrderDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>Order Details - {selectedOrder?.order_id}</span>
                {selectedOrder && getStatusBadge(selectedOrder.status)}
              </DialogTitle>
            </DialogHeader>
            
            {orderDetailsLoading ? (
              <div className="text-center py-8">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400" />
              </div>
            ) : selectedOrder && (
              <div className="space-y-6">
                {/* Order Info */}
                <div className="grid grid-cols-2 gap-4 text-sm bg-white border border-gray-200 rounded-lg p-4">
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Order Date</p>
                    <p className="font-medium text-gray-900">{formatDate(selectedOrder.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Shipping Address</p>
                    <p className="font-medium text-gray-900">{selectedOrder.shipping_address || '-'}</p>
                  </div>
                  {selectedOrder.preferred_transporter && (
                    <div>
                      <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Transporter</p>
                      <p className="font-medium text-gray-900">{selectedOrder.preferred_transporter}</p>
                    </div>
                  )}
                  {selectedOrder.notes && (
                    <div>
                      <p className="text-gray-500 text-xs uppercase tracking-wide mb-1">Notes</p>
                      <p className="font-medium text-gray-900">{selectedOrder.notes}</p>
                    </div>
                  )}
                </div>

                {/* Items Table - Excel Format with Basic Price */}
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Order Items</h3>
                    <Button
                      onClick={handleReorderAll}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      size="sm"
                    >
                      <ShoppingCart className="w-4 h-4 mr-2" />
                      Reorder All
                    </Button>
                  </div>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-red-600 text-white">
                          <TableHead className="text-white font-semibold text-center w-12">S No</TableHead>
                          <TableHead className="text-white font-semibold w-16">Image</TableHead>
                          <TableHead className="text-white font-semibold">Jp Part No</TableHead>
                          <TableHead className="text-white font-semibold">Part Name</TableHead>
                          <TableHead className="text-white font-semibold text-center">Qty</TableHead>
                          <TableHead className="text-white font-semibold text-right">Price</TableHead>
                          <TableHead className="text-white font-semibold text-right">Amount</TableHead>
                          <TableHead className="text-white font-semibold text-center w-16">Reorder</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedOrder.items?.map((item, idx) => {
                          const taxPaidPrice = item.discounted_price || item.price || item.mrp || 0;
                          const basicPrice = calculateBasicPrice(taxPaidPrice);
                          const basicAmount = basicPrice * item.quantity;
                          
                          return (
                            <TableRow key={idx} className="hover:bg-gray-50 border-b">
                              <TableCell className="text-center font-medium">{idx + 1}</TableCell>
                              <TableCell>
                                <div 
                                  className="w-12 h-12 rounded-md overflow-hidden border bg-gray-100 cursor-pointer hover:opacity-80 flex-shrink-0"
                                  onClick={() => item.image && setZoomImage(item.image)}
                                >
                                  {item.image ? (
                                    <img 
                                      src={item.image} 
                                      alt={item.part_name}
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                      <Package className="w-5 h-5" />
                                    </div>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="font-mono text-red-600 font-medium">
                                {item.new_jp_part_no || item.jp_part_no}
                                {item.new_jp_part_no && item.new_jp_part_no !== item.jp_part_no && (
                                  <span className="text-xs text-gray-400 font-normal ml-1">({item.jp_part_no})</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div>
                                  <p className="font-medium text-gray-900">{item.part_name}</p>
                                  {item.oe_part_no && (
                                    <p className="text-xs text-gray-500">{formatOeDisplay(item.oe_part_no, true)}</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-center font-medium">{item.quantity}</TableCell>
                              <TableCell className="text-right">
                                <div>
                                  <span>₹{basicPrice.toFixed(2)}</span>
                                  {isIndianCustomer && (
                                    <p className="text-[10px] text-gray-400">(+18% GST)</p>
                                  )}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-medium">₹{basicAmount.toFixed(2)}</TableCell>
                              <TableCell className="text-center">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleReorder(item)}
                                  className="text-emerald-600 border-emerald-300 hover:bg-emerald-50 h-8 w-8 p-0"
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {/* Order Summary - GST Breakdown */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                  <h4 className="text-sm font-semibold text-gray-700 mb-3">Order Summary</h4>
                  {(() => {
                    const totalBasicAmount = selectedOrder.items?.reduce((sum, item) => {
                      const taxPaidPrice = item.discounted_price || item.price || item.mrp || 0;
                      const basicPrice = calculateBasicPrice(taxPaidPrice);
                      return sum + (basicPrice * item.quantity);
                    }, 0) || 0;
                    
                    const gstAmount = totalBasicAmount * 0.18;
                    const totalAmount = totalBasicAmount + gstAmount;
                    
                    return (
                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Total Basic Amount</span>
                          <span className="text-gray-900">₹{totalBasicAmount.toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">GST (18%)</span>
                          <span className="text-gray-900">₹{gstAmount.toFixed(2)}</span>
                        </div>
                        {selectedOrder.discount_applied > 0 && (
                          <div className="flex justify-between text-sm text-emerald-600">
                            <span>Discount Applied</span>
                            <span className="font-medium">-₹{selectedOrder.discount_applied?.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between font-bold text-lg pt-3 mt-2 border-t border-slate-300">
                          <span className="text-gray-800">TOTAL AMOUNT</span>
                          <span className="text-red-600">₹{totalAmount.toFixed(2)}</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Download Documents Section - for dispatched/closed orders */}
                {['dispatched', 'closed', 'delivered', 'shipped'].includes(selectedOrder?.status?.toLowerCase()) && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 mt-4">
                    <h3 className="font-semibold text-green-800 mb-3 flex items-center">
                      <FileDown className="w-5 h-5 mr-2" />
                      Order Documents
                    </h3>
                    
                    {/* All Documents */}
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">All Documents</p>
                      <div className="flex flex-wrap gap-2">
                        <Button onClick={() => downloadOrderDocuments(selectedOrder.order_id)}
                          className="bg-green-600 hover:bg-green-700 text-white" size="sm" data-testid="dialog-download-all">
                          <FileDown className="w-4 h-4 mr-1" /> Download All (PDF)
                        </Button>
                      </div>
                    </div>
                    
                    {/* Invoice Actions */}
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Invoice</p>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => viewInvoice(selectedOrder.order_id)}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="dialog-view-invoice">
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => printDocDirect(selectedOrder.order_id, 'invoice')}
                          className="border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="dialog-print-invoice">
                          <Printer className="w-4 h-4 mr-1" /> Print
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => downloadTallyInvoice(selectedOrder.order_id)}
                          className="border-green-300 text-green-700 hover:bg-green-50" data-testid="dialog-download-invoice">
                          <Download className="w-4 h-4 mr-1" /> Download
                        </Button>
                      </div>
                    </div>

                    {/* Packing List Actions */}
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Packing List</p>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => downloadPackingList(selectedOrder.order_id)}
                          className="border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="dialog-download-packing">
                          <Download className="w-4 h-4 mr-1" /> Download
                        </Button>
                      </div>
                    </div>

                    {/* LR Actions */}
                    <div className="mb-3">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Lorry Receipt (LR)</p>
                      <div className="flex flex-wrap gap-2">
                        <Button variant="outline" size="sm" onClick={() => viewLR(selectedOrder.order_id)}
                          className="border-blue-300 text-blue-700 hover:bg-blue-50" data-testid="dialog-view-lr">
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => printDocDirect(selectedOrder.order_id, 'lr')}
                          className="border-gray-300 text-gray-700 hover:bg-gray-50" data-testid="dialog-print-lr">
                          <Printer className="w-4 h-4 mr-1" /> Print
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => downloadLRDetails(selectedOrder.order_id)}
                          className="border-green-300 text-green-700 hover:bg-green-50" data-testid="dialog-download-lr">
                          <Download className="w-4 h-4 mr-1" /> Download
                        </Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Order Modal for Customers */}
        <EditOrderModal
          order={editOrder}
          open={!!editOrder}
          onClose={() => setEditOrder(null)}
          onSave={() => {
            setEditOrder(null);
            loadOrders(page);
          }}
          isCustomer={true}
        />

        {/* Document Viewer Dialog */}
        <Dialog open={docViewerOpen} onOpenChange={(open) => { 
          if (!open) { 
            // Revoke Blob URL to prevent memory leak
            if (docViewerUrl && docViewerUrl.startsWith('blob:')) {
              URL.revokeObjectURL(docViewerUrl);
            }
            setDocViewerOpen(false); 
            setDocViewerUrl(null); 
          } 
        }}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" />
                {docViewerType === 'invoice' ? 'Invoice' : docViewerType === 'all' ? 'All Documents' : docViewerType === 'packing' ? 'Packing List' : 'Lorry Receipt'} - {docViewerOrderId}
              </DialogTitle>
            </DialogHeader>
            {docViewerLoading ? (
              <div className="text-center py-16">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">Loading document...</p>
              </div>
            ) : docViewerUrl ? (
              <div className="space-y-4">
                {/* Action buttons */}
                <div className="flex gap-2 justify-end border-b pb-3">
                  <Button variant="outline" size="sm" onClick={() => printDocument(docViewerUrl)} data-testid="doc-viewer-print">
                    <Printer className="w-4 h-4 mr-1" /> Print
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadDocument(docViewerUrl, `${docViewerType}_${docViewerOrderId}`)} data-testid="doc-viewer-download">
                    <Download className="w-4 h-4 mr-1" /> Download
                  </Button>
                </div>
                {/* Document preview - use object tag for better PDF rendering */}
                <div className="border rounded-lg overflow-hidden bg-gray-100 min-h-[400px] flex items-center justify-center">
                  {docViewerUrl.startsWith('data:application/pdf') || docViewerUrl.startsWith('blob:') || docViewerUrl.endsWith('.pdf') ? (
                    <iframe 
                      src={docViewerUrl} 
                      className="w-full h-[70vh]"
                      title="Document Viewer"
                    />
                  ) : docViewerUrl.startsWith('data:image') ? (
                    <img src={docViewerUrl} alt={docViewerType} className="max-w-full max-h-[70vh] object-contain" />
                  ) : (
                    <iframe 
                      src={docViewerUrl} 
                      className="w-full h-[70vh]"
                      title="Document Viewer"
                    />
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">No document available</div>
            )}
          </DialogContent>
        </Dialog>

        {/* LR Upload Dialog */}
        <Dialog open={lrUploadOpen} onOpenChange={(open) => { if (!open) { setLrUploadOpen(false); } }}>
          <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5" />
                Upload Lorry Receipt - {lrUploadOrderId}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-2">
              <div>
                <Label htmlFor="transport_name">Transport Name *</Label>
                <Input
                  id="transport_name"
                  placeholder="Enter transport company name"
                  value={lrFormData.transport_name}
                  onChange={(e) => setLrFormData(prev => ({ ...prev, transport_name: e.target.value }))}
                  data-testid="lr-transport-name"
                />
              </div>
              <div>
                <Label htmlFor="lr_number">LR Number *</Label>
                <Input
                  id="lr_number"
                  placeholder="Enter LR number"
                  value={lrFormData.lr_number}
                  onChange={(e) => setLrFormData(prev => ({ ...prev, lr_number: e.target.value }))}
                  data-testid="lr-number-input"
                />
              </div>
              <div>
                <Label htmlFor="lr_date">LR Date</Label>
                <Input
                  id="lr_date"
                  type="date"
                  value={lrFormData.lr_date}
                  onChange={(e) => setLrFormData(prev => ({ ...prev, lr_date: e.target.value }))}
                  data-testid="lr-date-input"
                />
              </div>
              <div>
                <Label htmlFor="lr_file">LR Copy (PDF/Image)</Label>
                <Input
                  id="lr_file"
                  type="file"
                  accept="application/pdf,image/*"
                  onChange={handleLrFileChange}
                  data-testid="lr-file-input"
                />
                {lrFormData.lr_image && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                    <CheckCircle className="w-3 h-3" /> File selected
                  </p>
                )}
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  onClick={uploadLR}
                  disabled={lrUploading || !lrFormData.transport_name || !lrFormData.lr_number}
                  className="bg-green-600 hover:bg-green-700 text-white flex-1"
                  data-testid="lr-upload-submit"
                >
                  {lrUploading ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload LR</>}
                </Button>
                <Button variant="outline" onClick={() => setLrUploadOpen(false)} data-testid="lr-upload-cancel">
                  Cancel
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Image Zoom Dialog */}
        <Dialog open={!!zoomImage} onOpenChange={() => setZoomImage(null)}>
          <DialogContent className="max-w-3xl p-2 bg-black/90">
            <button
              className="absolute top-2 right-2 z-10 bg-white/20 hover:bg-white/40 rounded-full p-2 text-white"
              onClick={() => setZoomImage(null)}
            >
              <X className="w-6 h-6" />
            </button>
            {zoomImage && (
              <img
                src={zoomImage}
                alt="Product"
                className="w-full h-auto max-h-[80vh] object-contain rounded"
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default MyOrdersPage;
